import { ItemFilterInitializer } from "../../../../../entities/filters/ItemFilter";
import { RollRequest, RollResponse } from "../../../../../entities/rolls/Roll";
import { AppThunkAction } from "../../../ApplicationState";
import { CommunalDice } from "../../../../../entities/adventures/Adventure";
import { ToastAction, ToastDispatchables } from "../../../toasts/Toasts.Actions";
import { ErrorToast } from "../../../../../entities/toasts/Toasts";
import { ApiDefaultState } from "../../../api/ApiStore.State";
import { LocalCharacterInitializer } from "../../../../../entities/characters/Character";
import { AdventureHub } from "../../../../../apis/account/Hub.Adventure";

// export type SheetSet = { type: 'CHARACTER_SET', character: CharacterInitializer }
export type CharacterSetEntityPending = { type: 'CHARACTER_SET_PENDING', characterId: string, entityId: string, pending: boolean }
export type CharacterSetPreviewItemInstanceId = { type: 'CHARACTER_SET_PREVIEW_ITEM_INSTANCE_ID', characterId: string, previewItemInstanceId?: string }
export type CharacterSetPreviewSlotId = { type: 'CHARACTER_SET_PREVIEW_SLOT_ID', characterId: string, previewSlotId?: string, previewSlotItemInstanceId?: string }
export type CharacterShowItemAdder = { type: 'CHARACTER_SHOW_ITEM_ADDER', characterId: string }
export type CharacterHideItemAdder = { type: 'CHARACTER_HIDE_ITEM_ADDER', characterId: string }
export type CharacterFilterItems = { type: 'CHARACTER_FILTER_ITEMS', characterId: string, filter: ItemFilterInitializer }
export type CharacterSetRolling = { type: 'CHARACTER_SET_ROLLING', characterId: string, rollRequest: RollRequest | undefined }
export type CharacterSetHand = { type: 'CHARACTER_SET_HAND', characterId: string, hand: RollResponse }
export type CharacterResetHand = { type: 'CHARACTER_RESET_HAND', characterId: string };
export type CharacterSetCommunalRolls = { type: 'CHARACTER_SET_COMMUNAL_DICE', characterId: string, communalDice: CommunalDice }
export type CharacterSetRollSkill = { type: 'CHARACTER_SET_ROLL_SKILL', characterId: string, skillId: string }
export type CharacterClearRollSkill = { type: 'CHARACTER_CLEAR_ROLL_SKILL', characterId: string }

export type CharacterSpecificAction = CharacterSetEntityPending
    | CharacterSetPreviewItemInstanceId
    | CharacterSetPreviewSlotId
    | CharacterShowItemAdder
    | CharacterHideItemAdder
    | CharacterFilterItems
    | CharacterSetRolling
    | CharacterSetHand
    | CharacterResetHand
    | CharacterSetCommunalRolls
    | CharacterSetRollSkill
    | CharacterClearRollSkill;

export const CharacterSpecificActions = {
    setPreviewItem: (character: LocalCharacterInitializer, previewItemInstanceId: string): AppThunkAction<CharacterSetPreviewItemInstanceId> => 
        (dispatch, getState) => {
            const {previewItemInstanceId: currentPreviewId} = character;
            if (currentPreviewId != previewItemInstanceId) {
                dispatch({ type: 'CHARACTER_SET_PREVIEW_ITEM_INSTANCE_ID', characterId: character.id, previewItemInstanceId })
            }
        },
    
    clearPreviewItem: (character: LocalCharacterInitializer): AppThunkAction<CharacterSetPreviewItemInstanceId> =>
        (dispatch, getState) => {
            const {previewItemInstanceId} = character;
            if (previewItemInstanceId != undefined) {
                dispatch({ type: 'CHARACTER_SET_PREVIEW_ITEM_INSTANCE_ID', characterId: character.id });
            }
        },

    setPreviewSlot: (character: LocalCharacterInitializer, previewSlotId: string, previewSlotItemInstanceId: string): AppThunkAction<CharacterSetPreviewSlotId> =>
        (dispatch, getState) => {
            const { previewSlotId: currentPreviewSlotId } = character;
            if (currentPreviewSlotId != previewSlotId) {
                dispatch({ type: 'CHARACTER_SET_PREVIEW_SLOT_ID', characterId: character.id, previewSlotId, previewSlotItemInstanceId });
            }
        },

    clearPreviewSlot: (character: LocalCharacterInitializer): AppThunkAction<CharacterSetPreviewSlotId> =>
        (dispatch, getState) => {
            const { previewSlotId } = character;
            if (previewSlotId != undefined) {
                dispatch({ type: 'CHARACTER_SET_PREVIEW_SLOT_ID', characterId: character.id });
            }
        },

    showItemAdder: (character: LocalCharacterInitializer): AppThunkAction<CharacterShowItemAdder> =>
        (dispatch) => {
            dispatch({ type: 'CHARACTER_SHOW_ITEM_ADDER', characterId: character.id });
        },

    hideItemAdder: (character: LocalCharacterInitializer): AppThunkAction<CharacterHideItemAdder> =>
        (dispatch) => {
            dispatch({ type: 'CHARACTER_HIDE_ITEM_ADDER', characterId: character.id });
        },

    filterItems: (character: LocalCharacterInitializer, filter: ItemFilterInitializer): AppThunkAction<CharacterFilterItems> =>
        (dispatch) => {
            dispatch({ type: 'CHARACTER_FILTER_ITEMS', characterId: character.id, filter: { ...filter, loaded: true } });
        },
    
    rollHand: (character: LocalCharacterInitializer, rollRequest: RollRequest): AppThunkAction<CharacterSetHand | CharacterSetRolling | ToastAction> =>
        async (dispatch, getState) => {
            try {
                const hub = AdventureHub.Instance();
                const response = await hub.rollForPlayer(character.id, rollRequest);
                if (response.status == 'Success') {
                    dispatch({ type: 'CHARACTER_SET_HAND', characterId: character.id, hand: response.payload });
                }
                else {
                    ToastDispatchables.toastValidationResults(response.validationResults, dispatch);
                }
            }
            catch (e) {
                ToastDispatchables.toastLegacy(new ErrorToast("Error rolling dice."), dispatch);
            }
            finally {
                dispatch({ type: 'CHARACTER_SET_ROLLING', characterId: character.id, rollRequest: undefined });
            }
        },

    resetHand: (character: LocalCharacterInitializer): AppThunkAction<CharacterResetHand> => 
        (dispatch) => dispatch({ type: 'CHARACTER_RESET_HAND', characterId: character.id }),

    setCommunalRolls: (character: LocalCharacterInitializer, communalDice: CommunalDice): AppThunkAction<CharacterSetCommunalRolls> =>
        (dispatch) => dispatch({ type: 'CHARACTER_SET_COMMUNAL_DICE', characterId: character.id, communalDice }),

    setRollSkill: (character: LocalCharacterInitializer, skillId: string): AppThunkAction<CharacterSetRollSkill> =>
        (dispatch) => {
            dispatch({ type: 'CHARACTER_SET_ROLL_SKILL', characterId: character.id, skillId });
        },
    
    clearRollSkill: (character: LocalCharacterInitializer): AppThunkAction<CharacterClearRollSkill> =>
        (dispatch) => {
            dispatch({ type: 'CHARACTER_CLEAR_ROLL_SKILL', characterId: character.id });
        },
}